import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { BotUser, Lang, Session } from '@fakerpg/core';
import { translate, type MessageKey, type Vars } from '../i18n/index.js';
import type { Prefs } from './prefs.js';
import type { GameStore, Snapshot } from './store.js';

const StoreContext = createContext<GameStore | null>(null);

export function StoreProvider({ store, children }: { store: GameStore; children: ReactNode }) {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

export function useStore(): GameStore {
  const store = useContext(StoreContext);
  if (!store) throw new Error('StoreProvider missing');
  return store;
}

export function useSnapshot(): Snapshot {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

/** The loaded bot account; screens below the game shell can rely on it. */
export function useSave(): BotUser {
  const state = useSnapshot().state;
  if (!state) throw new Error('no account loaded');
  return state.user;
}

/** In-progress interactions (adventure run, Spot round). */
export function useSession(): Session {
  const state = useSnapshot().state;
  if (!state) throw new Error('no account loaded');
  return state.session;
}

export function usePrefs(): Prefs {
  return useSnapshot().prefs;
}

export function useLang(): Lang {
  return useSnapshot().prefs.lang;
}

export function useT(): (key: MessageKey, vars?: Vars) => string {
  const lang = useLang();
  return (key, vars) => translate(lang, key, vars);
}

/** Re-renders every `intervalMs` with the store clock — for countdowns. */
export function useNow(intervalMs = 1000): number {
  const store = useStore();
  const [now, setNow] = useState(() => store.now());
  useEffect(() => {
    const id = setInterval(() => setNow(store.now()), intervalMs);
    return () => clearInterval(id);
  }, [store, intervalMs]);
  return now;
}
